import React, { useEffect, useState, useRef } from 'react';
import { FaDesktop, FaSpinner, FaTimes } from 'react-icons/fa';
import {
  Stage,
  LocalStageStream,
  StageEvents,
  StageConnectionState,
  SubscribeType
} from 'amazon-ivs-web-broadcast';
import logger from '../../utils/logger';
import ScreenShareTour, { hasCompletedScreenShareTutorial } from './ScreenShareTour';
import { createScreenShareAnnouncements } from './ivsScreenShareUnityBridge';
import UnityGameDockApp from './UnityGameDockApp';
import './UnityGame.css';

export type ScreenShareQuality = 'high' | 'medium' | 'low' | 'lower' | 'slideshow';

interface ScreenShareQualityConfig {
  label: string;
  description: string;
  width: number;
  height: number;
  frameRate: number;
}

export const SCREEN_SHARE_QUALITY_PRESETS: Record<ScreenShareQuality, ScreenShareQualityConfig> = {
  high: {
    label: 'High Quality',
    description: '720p @ 30fps',
    width: 1280,
    height: 720,
    frameRate: 30
  },
  medium: {
    label: 'Medium',
    description: '720p @ 24fps',
    width: 1280,
    height: 720,
    frameRate: 24
  },
  low: {
    label: 'Low Bandwidth',
    description: '480p @ 30fps',
    width: 854,
    height: 480,
    frameRate: 30
  },
  lower: {
    label: 'Lower Bandwidth',
    description: '480p @ 24fps',
    width: 854,
    height: 480,
    frameRate: 24
  },
  slideshow: {
    label: 'Slide Shows',
    description: '1080p @ 5fps',
    width: 1920,
    height: 1080,
    frameRate: 5
  }
};

export interface IvsScreenSharePublisherPanelProps {
  stageArn?: string;
  participantToken?: string;
  userId?: string;
  onScreenShareChange?: (isSharing: boolean) => void;
  hasScreenSharePermission?: boolean;
  debugMode?: boolean;
  /**
   * When true (e.g. Unity sandbox), treat the publish stage as ready after join() succeeds even if
   * connection callbacks were missed — still requires a valid token + successful join.
   */
  relaxConnectedGateForSandbox?: boolean;
  /** @deprecated Presentation Mode was removed. Accepted temporarily for older sandbox call sites. */
  isPresentationMode?: boolean;
  /** @deprecated Presentation Mode was removed. Accepted temporarily for older sandbox call sites. */
  onTogglePresentationMode?: () => void;
  /** Used to dismiss the dock popover and reset permission attention on room changes. */
  roomId?: string;
}

function setPresenterPlaybackSuppression(active: boolean, appUserId: string): void {
  const w = window as unknown as {
    __ffIvsSuppressScreenPlaybackActive?: boolean;
    __ffIvsSuppressScreenPlaybackAppUserId?: string;
  };
  if (active && appUserId) {
    w.__ffIvsSuppressScreenPlaybackActive = true;
    w.__ffIvsSuppressScreenPlaybackAppUserId = appUserId;
  } else {
    w.__ffIvsSuppressScreenPlaybackActive = false;
    w.__ffIvsSuppressScreenPlaybackAppUserId = '';
  }
}

/**
 * Publish-only IVS Realtime Stage for screen share: viewers consume via Unity AVPro + ivs.js;
 * React does not subscribe to remotes (no duplicate decoded audio in the browser).
 */
const IvsScreenSharePublisherPanel: React.FC<IvsScreenSharePublisherPanelProps> = ({
  stageArn,
  participantToken,
  userId,
  onScreenShareChange,
  hasScreenSharePermission = false,
  debugMode = false,
  relaxConnectedGateForSandbox = false,
  roomId
}) => {
  const [screenShareEnabled, setScreenShareEnabled] = useState(false);
  const [screenShareLoading, setScreenShareLoading] = useState(false);
  const [screenShareAudioEnabled, setScreenShareAudioEnabled] = useState(false);
  const [screenShareAudioMuted, setScreenShareAudioMuted] = useState(false);
  const [screenShareQuality, setScreenShareQuality] = useState<ScreenShareQuality>('medium');
  const [availableAudioDevices, setAvailableAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedAudioDeviceId, setSelectedAudioDeviceId] = useState<string>('');
  const [isScreenShareStageConnected, setIsScreenShareStageConnected] = useState(false);
  /** True after stage.join() resolves without throwing (publish stage is usable). */
  const [screenShareJoinSucceeded, setScreenShareJoinSucceeded] = useState(false);
  const [showScreenShareTutorial, setShowScreenShareTutorial] = useState(false);
  const [isDockOpen, setIsDockOpen] = useState(false);
  const [needsPermissionAttention, setNeedsPermissionAttention] = useState(false);
  const previousPermissionRef = useRef(false);

  const screenShareStageReady =
    isScreenShareStageConnected ||
    (relaxConnectedGateForSandbox && screenShareJoinSucceeded);

  const screenShareStageRef = useRef<any>(null);
  const screenShareStreamRef = useRef<LocalStageStream | null>(null);
  const screenShareMediaStreamRef = useRef<MediaStream | null>(null);
  const screenShareAudioStreamRef = useRef<LocalStageStream | null>(null);
  const screenShareMicStreamRef = useRef<MediaStream | null>(null);
  const screenShareSystemAudioStreamRef = useRef<LocalStageStream | null>(null);
  const screenShareAudioContextRef = useRef<AudioContext | null>(null);
  const screenShareProcessedStreamRef = useRef<MediaStream | null>(null);
  const screenShareStreamsToPublishRef = useRef<any[]>([]);
  const announcementsRef = useRef<ReturnType<typeof createScreenShareAnnouncements>>();
  if (!announcementsRef.current) announcementsRef.current = createScreenShareAnnouncements();
  const stageGenerationRef = useRef(0);

  function notifyUnityPublisherInactive() {
    announcementsRef.current?.stop();
    setPresenterPlaybackSuppression(false, '');
  }

  // Fetch audio devices (screen-share mic)
  useEffect(() => {
    const fetchAudioDevices = async () => {
      try {
        await navigator.mediaDevices
          .getUserMedia({ audio: true })
          .then((stream) => {
            stream.getTracks().forEach((track) => track.stop());
          })
          .catch(() => {});

        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter((device) => device.kind === 'audioinput');
        setAvailableAudioDevices(audioInputs);

        if (!selectedAudioDeviceId && audioInputs.length > 0) {
          setSelectedAudioDeviceId(audioInputs[0].deviceId);
        }
      } catch (error) {
        console.error('[IVS ScreenShare] Error fetching audio devices:', error);
      }
    };

    fetchAudioDevices();

    const handler = () => fetchAudioDevices();
    navigator.mediaDevices?.addEventListener?.('devicechange', handler);
    return () => navigator.mediaDevices?.removeEventListener?.('devicechange', handler);
  }, [selectedAudioDeviceId]);

  useEffect(() => {
    if (hasScreenSharePermission && !previousPermissionRef.current) {
      setNeedsPermissionAttention(true);
    }
    if (!hasScreenSharePermission) {
      setNeedsPermissionAttention(false);
      setIsDockOpen(false);
    }
    previousPermissionRef.current = hasScreenSharePermission;
  }, [hasScreenSharePermission]);

  useEffect(() => {
    setIsDockOpen(false);
    setNeedsPermissionAttention(false);
  }, [roomId]);

  useEffect(() => {
    logger.log('[IVS ScreenShare] Initialize publish-only stage', {
      hasStageArn: !!stageArn,
      hasParticipantToken: !!participantToken,
      userId
    });

    if (!stageArn || !participantToken) {
      logger.log('[IVS ScreenShare] Skip stage — missing stageArn or participantToken');
      setScreenShareJoinSucceeded(false);
      return;
    }

    setScreenShareJoinSucceeded(false);
    const generation = ++stageGenerationRef.current;
    let connectionPollInterval: ReturnType<typeof setInterval> | undefined;
    let connectionPollEndTimer: ReturnType<typeof setTimeout> | undefined;

    const connectToScreenShareStage = async () => {
      try {
        const strategy: any = {
          shouldPublishParticipant: (): boolean => {
            const shouldPublish = screenShareStreamsToPublishRef.current.length > 0;
            logger.log('[IVS ScreenShare] shouldPublishParticipant', shouldPublish);
            return shouldPublish;
          },
          stageStreamsToPublish: (): any[] => {
            logger.log(
              '[IVS ScreenShare] stageStreamsToPublish',
              screenShareStreamsToPublishRef.current.length
            );
            return screenShareStreamsToPublishRef.current;
          },
          /** Publish-only: Unity AVPro is the subscriber for viewers. */
          shouldSubscribeToParticipant: (): SubscribeType => SubscribeType.NONE
        };

        const stage = new Stage(participantToken, strategy);
        screenShareStageRef.current = stage;

        const stageAny = stage as any;

        const connectionStateHandler = (state: unknown) => {
          if (generation !== stageGenerationRef.current) return;
          if (state === StageConnectionState.CONNECTED) {
            setIsScreenShareStageConnected(true);
            logger.log('[IVS ScreenShare] Stage connected');
            return;
          }
          if (state === StageConnectionState.DISCONNECTED) {
            setIsScreenShareStageConnected(false);
            return;
          }
          if (state === StageConnectionState.ERRORED) {
            console.error('[IVS ScreenShare] Stage connection errored');
            setIsScreenShareStageConnected(false);
            return;
          }
          const stateStr =
            typeof state === 'string'
              ? state
              : (state as { state?: string })?.state ??
                (state as { connectionState?: string })?.connectionState ??
                String(state);
          if (stateStr === 'connected' || stateStr === 'CONNECTED') {
            setIsScreenShareStageConnected(true);
            logger.log('[IVS ScreenShare] Stage connected (legacy)');
          } else if (stateStr === 'disconnected' || stateStr === 'DISCONNECTED') {
            setIsScreenShareStageConnected(false);
          } else if (stateStr === 'failed' || stateStr === 'FAILED' || stateStr === 'errored') {
            console.error('[IVS ScreenShare] Stage connection failed');
            setIsScreenShareStageConnected(false);
          }
        };

        // Official IVS Realtime event (string-based listeners above never fire for this SDK)
        stageAny.on(StageEvents.STAGE_CONNECTION_STATE_CHANGED, connectionStateHandler);
        stageAny.on('connection-state-changed', connectionStateHandler);
        stageAny.on('connectionStateChanged', connectionStateHandler);
        stageAny.on('state-changed', connectionStateHandler);

        if (typeof stageAny.onConnectionStateChanged === 'function') {
          stageAny.onConnectionStateChanged(connectionStateHandler);
        }

        stageAny.on('error', (error: any) => {
          if (generation !== stageGenerationRef.current) return;
          console.error('[IVS ScreenShare] Stage error:', error);
          setIsScreenShareStageConnected(false);
        });

        if (debugMode) {
          stageAny.on(StageEvents.STAGE_PARTICIPANT_STREAMS_ADDED, (participant: any, streams: any[]) => {
            if (participant && !participant.isLocal) {
              logger.warn('[IVS ScreenShare] Remote streams received in publish-only mode (unexpected)', {
                participantId: participant.id,
                streamCount: streams?.length
              });
            }
          });
        }

        if (typeof stageAny.join === 'function') {
          await stageAny.join();
          if (generation !== stageGenerationRef.current) {
            stageAny.leave?.();
            return;
          }
          logger.log('[IVS ScreenShare] Joined stage');
        }

        setScreenShareJoinSucceeded(true);

        let initialState: StageConnectionState | string | undefined;
        if (typeof stageAny.getConnectionState === 'function') {
          initialState = stageAny.getConnectionState();
        } else if (stageAny.connectionState !== undefined) {
          initialState = stageAny.connectionState;
        }
        if (
          initialState === StageConnectionState.CONNECTED ||
          initialState === 'connected' ||
          initialState === 'CONNECTED'
        ) {
          setIsScreenShareStageConnected(true);
        }

        const pollConnection = () => {
          try {
            const cs =
              typeof stageAny.getConnectionState === 'function'
                ? stageAny.getConnectionState()
                : stageAny.connectionState;
            if (cs === StageConnectionState.CONNECTED || cs === 'connected' || cs === 'CONNECTED') {
              setIsScreenShareStageConnected(true);
            }
          } catch {
            /* ignore */
          }
        };
        connectionPollInterval = setInterval(pollConnection, 500);
        connectionPollEndTimer = setTimeout(() => {
          if (connectionPollInterval) {
            clearInterval(connectionPollInterval);
            connectionPollInterval = undefined;
          }
        }, 15000);
      } catch (error) {
        if (generation !== stageGenerationRef.current) return;
        console.error('[IVS ScreenShare] Error connecting:', error);
        setIsScreenShareStageConnected(false);
        setScreenShareJoinSucceeded(false);
      }
    };

    connectToScreenShareStage();

    return () => {
      ++stageGenerationRef.current;
      if (connectionPollInterval) {
        clearInterval(connectionPollInterval);
      }
      if (connectionPollEndTimer) {
        clearTimeout(connectionPollEndTimer);
      }
      setScreenShareJoinSucceeded(false);
      logger.log('[IVS ScreenShare] Cleanup stage');
      notifyUnityPublisherInactive();
      if (screenShareMediaStreamRef.current) onScreenShareChange?.(false);
      setScreenShareEnabled(false);
      setScreenShareLoading(false);
      setScreenShareAudioEnabled(false);
      setScreenShareAudioMuted(false);
      screenShareStreamRef.current = null;

      if (screenShareMediaStreamRef.current) {
        screenShareMediaStreamRef.current.getTracks().forEach((t) => t.stop());
        screenShareMediaStreamRef.current = null;
      }
      if (screenShareMicStreamRef.current) {
        screenShareMicStreamRef.current.getTracks().forEach((t) => t.stop());
        screenShareMicStreamRef.current = null;
      }
      if (screenShareProcessedStreamRef.current) {
        screenShareProcessedStreamRef.current.getTracks().forEach((t) => t.stop());
        screenShareProcessedStreamRef.current = null;
      }
      if (screenShareAudioContextRef.current) {
        screenShareAudioContextRef.current.close().catch(() => {});
        screenShareAudioContextRef.current = null;
      }

      screenShareStreamsToPublishRef.current = [];

      if (screenShareStageRef.current) {
        const stageAny = screenShareStageRef.current as any;
        try {
          if (typeof stageAny.refreshStrategy === 'function') {
            const p = stageAny.refreshStrategy();
            if (p && typeof p.then === 'function') {
              p.catch(() => {}).finally(() => {
                try {
                  if (stageAny.leave) stageAny.leave();
                  else if (stageAny.disconnect) stageAny.disconnect();
                } catch (e) {
                  console.error('[IVS ScreenShare] Error leaving stage:', e);
                }
              });
            } else {
              if (stageAny.leave) stageAny.leave();
              else if (stageAny.disconnect) stageAny.disconnect();
            }
          } else {
            if (stageAny.leave) stageAny.leave();
            else if (stageAny.disconnect) stageAny.disconnect();
          }
        } catch (e) {
          console.error('[IVS ScreenShare] Error leaving stage:', e);
        }
        screenShareStageRef.current = null;
      }

      setIsScreenShareStageConnected(false);
    };
  }, [stageArn, participantToken, userId, debugMode, roomId]);

  const toggleScreenShare = async () => {
    if (!screenShareStageRef.current || !screenShareStageReady) {
      logger.log('[IVS ScreenShare] Stage not ready');
      return;
    }

    if (!screenShareEnabled && !hasCompletedScreenShareTutorial()) {
      setShowScreenShareTutorial(true);
      return;
    }

    const generation = stageGenerationRef.current;
    const publishingStage = screenShareStageRef.current;
    try {
      if (screenShareEnabled) {
        logger.log('[IVS ScreenShare] Stopping screen share');
        const stageAny = screenShareStageRef.current as any;
        const streamToUnpublish = screenShareStreamRef.current;
        const audioStreamToUnpublish = screenShareAudioStreamRef.current;

        notifyUnityPublisherInactive();

        try {
          if (screenShareMediaStreamRef.current) {
            screenShareMediaStreamRef.current.getTracks().forEach((track) => track.stop());
          }

          if (screenShareMicStreamRef.current) {
            screenShareMicStreamRef.current.getTracks().forEach((track) => track.stop());
            screenShareMicStreamRef.current = null;
          }

          if (streamToUnpublish) {
            screenShareStreamsToPublishRef.current = screenShareStreamsToPublishRef.current.filter(
              (s) => s !== streamToUnpublish
            );
          }
          if (audioStreamToUnpublish) {
            screenShareStreamsToPublishRef.current = screenShareStreamsToPublishRef.current.filter(
              (s) => s !== audioStreamToUnpublish
            );
          }
          if (screenShareSystemAudioStreamRef.current) {
            screenShareStreamsToPublishRef.current = screenShareStreamsToPublishRef.current.filter(
              (s) => s !== screenShareSystemAudioStreamRef.current
            );
            screenShareSystemAudioStreamRef.current = null;
          }

          if (screenShareAudioContextRef.current) {
            screenShareAudioContextRef.current.close().catch(() => {});
            screenShareAudioContextRef.current = null;
          }
          if (screenShareProcessedStreamRef.current) {
            screenShareProcessedStreamRef.current.getTracks().forEach((t) => t.stop());
            screenShareProcessedStreamRef.current = null;
          }

          if (typeof stageAny.refreshStrategy === 'function') {
            await stageAny.refreshStrategy();
          }

          screenShareStreamRef.current = null;
          screenShareMediaStreamRef.current = null;
          screenShareAudioStreamRef.current = null;
        } catch (error) {
          console.error('[IVS ScreenShare] Error unpublishing:', error);
        }

        setScreenShareEnabled(false);
        setScreenShareAudioEnabled(false);
        setScreenShareAudioMuted(false);
        setScreenShareLoading(false);
        onScreenShareChange?.(false);
      } else {
        logger.log('[IVS ScreenShare] Starting screen share');
        setScreenShareLoading(true);

        const qualityConfig = SCREEN_SHARE_QUALITY_PRESETS[screenShareQuality];
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            width: { ideal: qualityConfig.width },
            height: { ideal: qualityConfig.height },
            frameRate: { ideal: qualityConfig.frameRate }
          },
          audio: {
            channelCount: { ideal: 2 },
            sampleRate: { ideal: 48000 },
            sampleSize: { ideal: 16 },
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false
          }
        });

        if (generation !== stageGenerationRef.current || publishingStage !== screenShareStageRef.current) {
          stream.getTracks().forEach(track => track.stop());
          return;
        }

        stream.getVideoTracks()[0].onended = () => {
          if (generation !== stageGenerationRef.current || screenShareMediaStreamRef.current !== stream) return;
          logger.log('[IVS ScreenShare] Display media ended by user');
          setScreenShareEnabled(false);
          setScreenShareLoading(false);
          setScreenShareAudioEnabled(false);
          setScreenShareAudioMuted(false);
          onScreenShareChange?.(false);
          notifyUnityPublisherInactive();

          if (screenShareStageRef.current) {
            const stageAny = screenShareStageRef.current as any;
            if (screenShareStreamRef.current) {
              screenShareStreamsToPublishRef.current = screenShareStreamsToPublishRef.current.filter(
                (s) => s !== screenShareStreamRef.current
              );
            }
            if (screenShareSystemAudioStreamRef.current) {
              screenShareStreamsToPublishRef.current = screenShareStreamsToPublishRef.current.filter(
                (s) => s !== screenShareSystemAudioStreamRef.current
              );
              screenShareSystemAudioStreamRef.current = null;
            }
            if (screenShareAudioContextRef.current) {
              screenShareAudioContextRef.current.close().catch(() => {});
              screenShareAudioContextRef.current = null;
            }
            if (screenShareProcessedStreamRef.current) {
              screenShareProcessedStreamRef.current.getTracks().forEach((t) => t.stop());
              screenShareProcessedStreamRef.current = null;
            }
            if (screenShareAudioStreamRef.current) {
              screenShareStreamsToPublishRef.current = screenShareStreamsToPublishRef.current.filter(
                (s) => s !== screenShareAudioStreamRef.current
              );
              screenShareAudioStreamRef.current = null;
            }
            if (screenShareMicStreamRef.current) {
              screenShareMicStreamRef.current.getTracks().forEach((track) => track.stop());
              screenShareMicStreamRef.current = null;
            }
            if (typeof stageAny.refreshStrategy === 'function') {
              try {
                const result = stageAny.refreshStrategy();
                if (result && typeof result.catch === 'function') {
                  result.catch((e: any) => console.error('[IVS ScreenShare] refreshStrategy', e));
                }
              } catch (e) {
                console.error('[IVS ScreenShare] refreshStrategy', e);
              }
            }
          }

          screenShareStreamRef.current = null;
          screenShareMediaStreamRef.current = null;
        };

        screenShareMediaStreamRef.current = stream;
        const stageAny = screenShareStageRef.current as any;
        let localVideoStageStream: any;
        let localAudioStageStream: any = null;

        try {
          const videoTrack = stream.getVideoTracks()[0];
          if (!videoTrack) {
            throw new Error('No video track in screen share stream');
          }
          localVideoStageStream = new (LocalStageStream as any)(videoTrack);

          const audioTrack = stream.getAudioTracks()[0];
          if (audioTrack) {
            try {
              const audioContext = new AudioContext({ sampleRate: 48000 });
              screenShareAudioContextRef.current = audioContext;
              const source = audioContext.createMediaStreamSource(new MediaStream([audioTrack]));
              const compressor = audioContext.createDynamicsCompressor();
              compressor.threshold.setValueAtTime(-24, audioContext.currentTime);
              compressor.knee.setValueAtTime(30, audioContext.currentTime);
              compressor.ratio.setValueAtTime(4, audioContext.currentTime);
              compressor.attack.setValueAtTime(0.003, audioContext.currentTime);
              compressor.release.setValueAtTime(0.25, audioContext.currentTime);
              const gainNode = audioContext.createGain();
              gainNode.gain.setValueAtTime(1.2, audioContext.currentTime);
              source.connect(compressor);
              compressor.connect(gainNode);
              const destination = audioContext.createMediaStreamDestination();
              gainNode.connect(destination);
              screenShareProcessedStreamRef.current = destination.stream;
              const processedAudioTrack = destination.stream.getAudioTracks()[0];
              localAudioStageStream = new (LocalStageStream as any)(processedAudioTrack);
              screenShareSystemAudioStreamRef.current = localAudioStageStream;
            } catch (audioProcessingError) {
              console.warn('[IVS ScreenShare] Audio processing fallback to raw', audioProcessingError);
              localAudioStageStream = new (LocalStageStream as any)(audioTrack);
              screenShareSystemAudioStreamRef.current = localAudioStageStream;
            }
          }
        } catch (e) {
          console.error('[IVS ScreenShare] LocalStageStream failed:', e);
          stream.getTracks().forEach((track) => track.stop());
          setScreenShareLoading(false);
          throw e;
        }

        try {
          const streamsToAdd = [localVideoStageStream];
          if (localAudioStageStream) {
            streamsToAdd.push(localAudioStageStream);
          }
          screenShareStreamsToPublishRef.current = [...screenShareStreamsToPublishRef.current, ...streamsToAdd];

          if (typeof stageAny.refreshStrategy === 'function') {
            await stageAny.refreshStrategy();
            setTimeout(() => {
              if (
                generation === stageGenerationRef.current &&
                screenShareStageRef.current === publishingStage &&
                screenShareStreamRef.current &&
                typeof (screenShareStageRef.current as any).refreshStrategy === 'function'
              ) {
                try {
                  const delayed = (screenShareStageRef.current as any).refreshStrategy();
                  if (delayed != null && typeof delayed.then === 'function') {
                    delayed
                      .then(() => logger.log('[IVS ScreenShare] Delayed refreshStrategy done'))
                      .catch((err: any) => console.error('[IVS ScreenShare] Delayed refreshStrategy', err));
                  } else {
                    logger.log('[IVS ScreenShare] Delayed refreshStrategy (sync/void)');
                  }
                } catch (err: any) {
                  console.error('[IVS ScreenShare] Delayed refreshStrategy', err);
                }
              }
            }, 1200);
          } else {
            throw new Error('refreshStrategy not available');
          }
        } catch (publishError) {
          console.error('[IVS ScreenShare] Publish error:', publishError);
          stream.getTracks().forEach((track) => track.stop());
          setScreenShareLoading(false);
          throw publishError;
        }

        if (generation !== stageGenerationRef.current || stream.getVideoTracks()[0].readyState === 'ended') {
          stream.getTracks().forEach(track => track.stop());
          return;
        }
        screenShareStreamRef.current = localVideoStageStream;
        setScreenShareEnabled(true);
        setScreenShareLoading(false);
        onScreenShareChange?.(true);

        const lp =
          (stageAny.localParticipant?.id ?? stageAny.localParticipant?.info?.id ?? '') + '';
        if (lp && roomId && stageArn) {
          setPresenterPlaybackSuppression(true, String(userId || '').trim());
          announcementsRef.current?.start({ participantId: lp, roomId, stageArn,
            sessionId: `${Date.now()}-${Math.random().toString(36).slice(2)}` });
        }
      }
    } catch (error) {
      console.error('[IVS ScreenShare] toggleScreenShare error:', error);
      setScreenShareLoading(false);
      if ((error as Error).name === 'NotAllowedError') {
        logger.log('[IVS ScreenShare] User cancelled picker');
      }
    }
  };

  const toggleScreenShareAudio = async () => {
    if (!screenShareStageRef.current || !screenShareStageReady || !screenShareEnabled) return;

    try {
      if (screenShareAudioEnabled) {
        const stageAny = screenShareStageRef.current as any;

        if (screenShareMicStreamRef.current) {
          screenShareMicStreamRef.current.getTracks().forEach((track) => track.stop());
          screenShareMicStreamRef.current = null;
        }

        if (screenShareAudioStreamRef.current) {
          screenShareStreamsToPublishRef.current = screenShareStreamsToPublishRef.current.filter(
            (s) => s !== screenShareAudioStreamRef.current
          );

          if (typeof stageAny.refreshStrategy === 'function') {
            await stageAny.refreshStrategy();
          }

          screenShareAudioStreamRef.current = null;
        }

        setScreenShareAudioEnabled(false);
        setScreenShareAudioMuted(false);
      } else {
        const audioConstraints: MediaTrackConstraints = {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        };
        if (selectedAudioDeviceId) {
          audioConstraints.deviceId = { exact: selectedAudioDeviceId };
        }

        const micStream = await navigator.mediaDevices.getUserMedia({
          audio: audioConstraints,
          video: false
        });
        screenShareMicStreamRef.current = micStream;

        const audioTrack = micStream.getAudioTracks()[0];
        if (!audioTrack) {
          throw new Error('No mic audio track');
        }

        const localStageStream = new (LocalStageStream as any)(audioTrack);
        screenShareAudioStreamRef.current = localStageStream;

        screenShareStreamsToPublishRef.current = [...screenShareStreamsToPublishRef.current, localStageStream];

        const stageAny = screenShareStageRef.current as any;
        if (typeof stageAny.refreshStrategy === 'function') {
          await stageAny.refreshStrategy();
        }

        setScreenShareAudioEnabled(true);
        setScreenShareAudioMuted(false);
      }
    } catch (error) {
      console.error('[IVS ScreenShare] toggleScreenShareAudio:', error);
    }
  };

  const toggleScreenShareAudioMute = () => {
    if (!screenShareMicStreamRef.current) return;
    screenShareMicStreamRef.current.getAudioTracks().forEach((track) => {
      track.enabled = screenShareAudioMuted;
    });
    setScreenShareAudioMuted(!screenShareAudioMuted);
  };

  const changeScreenShareAudioDevice = async (deviceId: string) => {
    setSelectedAudioDeviceId(deviceId);

    if (screenShareAudioEnabled && screenShareMicStreamRef.current && screenShareStageRef.current) {
      const stageAny = screenShareStageRef.current as any;
      screenShareMicStreamRef.current.getTracks().forEach((track) => track.stop());

      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: { exact: deviceId },
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        },
        video: false
      });
      screenShareMicStreamRef.current = micStream;

      if (screenShareAudioStreamRef.current) {
        screenShareStreamsToPublishRef.current = screenShareStreamsToPublishRef.current.filter(
          (s) => s !== screenShareAudioStreamRef.current
        );
      }

      const audioTrack = micStream.getAudioTracks()[0];
      const localStageStream = new (LocalStageStream as any)(audioTrack);
      screenShareAudioStreamRef.current = localStageStream;

      screenShareStreamsToPublishRef.current = [...screenShareStreamsToPublishRef.current, localStageStream];

      if (typeof stageAny.refreshStrategy === 'function') {
        await stageAny.refreshStrategy();
      }

      if (screenShareAudioMuted) {
        audioTrack.enabled = false;
      }
    }
  };

  useEffect(() => {
    if (!hasScreenSharePermission && screenShareEnabled) {
      void toggleScreenShare();
    }
    // toggleScreenShare intentionally uses the current stream refs for teardown.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasScreenSharePermission, screenShareEnabled]);

  if (!hasScreenSharePermission) return null;

  return (
    <>
      <UnityGameDockApp
        ariaLabel="Screen Share — broadcast your screen"
        label="Share"
        icon={<FaDesktop />}
        active={isDockOpen}
        onRequestClose={() => setIsDockOpen(false)}
        badge={screenShareEnabled ? <span className="block h-2 w-2 rounded-full bg-emerald-300" /> : undefined}
        wrapperClassName={needsPermissionAttention ? 'animate-pulse' : undefined}
        tileClassName={`bg-gradient-to-br from-sky-500 to-blue-700 ${needsPermissionAttention ? 'ring-2 ring-sky-300 ring-offset-2 ring-offset-transparent' : ''}`}
        onClick={(event) => {
          event.stopPropagation();
          setNeedsPermissionAttention(false);
          setIsDockOpen((open) => !open);
        }}
      >
        {isDockOpen && (
          <div className="unity-dock-popover unity-dock-popover--enter unity-dock-popover--wide w-[min(92vw,28rem)] overflow-hidden" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <div>
                <h3 className="text-base font-semibold text-white">Screen Share</h3>
                <p className="text-[11px] text-white/70">Broadcast to the screens in this room</p>
              </div>
              <button type="button" onClick={() => setIsDockOpen(false)} className="rounded p-2 text-white/50 hover:bg-white/10 hover:text-white" aria-label="Close Screen Share">
                <FaTimes size={17} />
              </button>
            </div>
        <div className="p-4">
          <div className="space-y-2">
            {!screenShareEnabled && (
              <div data-tour-id="tour-screenshare-quality">
                <label htmlFor="screen-share-quality" className="mb-1.5 block text-xs font-medium text-white/80">
                  Stream quality
                </label>
                <select
                  id="screen-share-quality"
                  value={screenShareQuality}
                  onChange={(e) => setScreenShareQuality(e.target.value as ScreenShareQuality)}
                  className="w-full cursor-pointer rounded-md border border-white/20 bg-[#2a2a2a] p-2.5 text-sm text-white [color-scheme:dark] focus:border-blue-400 focus:outline-none disabled:cursor-not-allowed disabled:text-white/60"
                  disabled={!screenShareStageReady || screenShareLoading}
                >
                  {(Object.keys(SCREEN_SHARE_QUALITY_PRESETS) as ScreenShareQuality[]).map((key) => (
                    <option key={key} value={key}>
                      {SCREEN_SHARE_QUALITY_PRESETS[key].label} ({SCREEN_SHARE_QUALITY_PRESETS[key].description})
                    </option>
                  ))}
                </select>
                <p className="mt-1.5 text-[11px] leading-4 text-white/70">
                  Higher quality is sharper; lower quality performs better on slower connections.
                </p>
              </div>
            )}

            <button
              type="button"
              className={`w-full p-2.5 rounded-md text-white cursor-pointer flex items-center justify-center gap-2 transition-all duration-200 text-base ${
                screenShareEnabled
                  ? 'bg-green-500 border border-green-500 hover:bg-green-600'
                  : 'bg-[#2a2a2a] border border-[#444] hover:bg-[#3a3a3a] hover:border-[#555]'
              } ${!screenShareStageReady || screenShareLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={toggleScreenShare}
              disabled={!screenShareStageReady || screenShareLoading}
              title={
                !screenShareStageReady
                  ? 'Screen share stage not connected'
                  : screenShareEnabled
                    ? 'Stop sharing screen'
                    : `Share your screen (${SCREEN_SHARE_QUALITY_PRESETS[screenShareQuality].description})`
              }
              data-tour-id="tour-screenshare-button"
            >
              {screenShareLoading ? (
                <FaSpinner className="animate-spin" />
              ) : (
                <FaDesktop />
              )}
              <span className="text-sm font-medium">
                {screenShareLoading ? 'Starting...' : screenShareEnabled ? 'Stop Sharing' : 'Share Screen'}
              </span>
            </button>

            {screenShareEnabled && (
              <div className="flex flex-col gap-2 pt-1">
                <button
                  type="button"
                  className={`w-full p-2 rounded-md text-sm flex items-center justify-center gap-2 ${
                    screenShareAudioEnabled ? 'bg-blue-600 hover:bg-blue-700' : 'bg-[#2a2a2a] border border-[#444]'
                  } text-white`}
                  onClick={toggleScreenShareAudio}
                >
                  {screenShareAudioEnabled ? 'Stop mic (screen share)' : 'Add mic to screen share'}
                </button>
                {screenShareAudioEnabled && screenShareMicStreamRef.current && (
                  <button
                    type="button"
                    className="w-full p-2 rounded-md text-sm bg-[#2a2a2a] border border-[#444] text-white"
                    onClick={toggleScreenShareAudioMute}
                  >
                    {screenShareAudioMuted ? 'Unmute mic' : 'Mute mic'}
                  </button>
                )}
                {availableAudioDevices.length > 1 && (
                  <select
                    value={selectedAudioDeviceId}
                    onChange={(e) => changeScreenShareAudioDevice(e.target.value)}
                    className="rounded-md border border-white/20 bg-[#2a2a2a] p-2 text-xs text-white [color-scheme:dark]"
                  >
                    {availableAudioDevices.map((d) => (
                      <option key={d.deviceId} value={d.deviceId}>
                        {d.label || `Mic ${d.deviceId.slice(0, 8)}`}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>

        </div>

      {screenShareEnabled && screenShareMediaStreamRef.current && (
        <div className="relative bg-green-600/20 border-2 border-green-500 rounded-md p-3 w-full mt-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center animate-pulse">
              <FaDesktop size={18} className="text-white" />
            </div>
            <div className="flex-1">
              <div className="text-green-400 font-bold text-sm flex items-center gap-2">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                Screen Sharing Active
              </div>
              <div className="mt-0.5 text-xs text-emerald-50/80">Your screen is being broadcast to this room</div>
            </div>
          </div>
        </div>
      )}
          </div>
        )}
      </UnityGameDockApp>

      <ScreenShareTour
        isVisible={showScreenShareTutorial}
        onComplete={() => setShowScreenShareTutorial(false)}
        onSkip={() => setShowScreenShareTutorial(false)}
      />
    </>
  );
};

export default IvsScreenSharePublisherPanel;
